import React from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import { AppUI } from '@seedlang/state';
import YoutubePlayer from 'components/worksheet/youtube_player';
import PodcastButton from 'components/worksheet/podcast_button';
import { isPresent } from '@seedlang/utils';

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: none;
  border-radius: ${props => props.borderRadius};
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s linear;
  background: #000;

  &:hover {
    opacity: 0.9;
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: #222;
`;

const YoutubePlayButton = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 68px;
  z-index: 1;
`;

const DurationIndicator = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  border-radius: 4px;
  padding: 2px 4px;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  z-index: 1;
`;

function formatDuration(seconds) {
  const parsedSeconds = parseInt(seconds, 10);
  if (Number.isNaN(parsedSeconds) || parsedSeconds < 0) {
    return null;
  }

  const minutes = Math.floor(parsedSeconds / 60);
  const remainingSeconds = parsedSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export class YoutubeThumbnail extends React.Component {
  static defaultProps = {
    borderRadius: '10px',
    showPlayButton: true,
    title: 'Play video',
    wrapperElement: 'div',
  };

  render() {
    const formattedDuration = formatDuration(this.props.durationSeconds);

    return (
      <ThumbnailWrapper
        as={this.props.wrapperElement}
        type={this.props.wrapperElement === 'button' ? 'button' : undefined}
        onClick={this.props.onClick}
        title={this.props.title}
        aria-label={this.props.title}
        borderRadius={this.props.borderRadius}
      >
        {
          this.props.showPlayButton &&
            <YoutubePlayButton src='/images/youtube_play_button.svg' alt='' />
        }
        {
          isPresent(this.props.thumbnailUrl) ? (
            <Thumbnail
              src={this.props.thumbnailUrl}
              alt={this.props.thumbnailAlt || 'Video thumbnail'}
              className={this.props.imageClassName}
            />
          ) : (
            <ThumbnailPlaceholder />
          )
        }
        {
          isPresent(formattedDuration) &&
            <DurationIndicator>{formattedDuration}</DurationIndicator>
        }
      </ThumbnailWrapper>
    );
  }
}

@observer
class YoutubeThumbnailPlayer extends React.Component {
  static defaultProps = {
    borderRadius: '10px',
    title: 'Play video',
    width: '540px',
  };

  state = {
    showPlayer: false,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.url !== this.props.url && this.state.showPlayer) {
      this.setState({ showPlayer: false });
    }
  }

  handleThumbnailClick = () => {
    this.setState({ showPlayer: true });
    if (this.props.onPlay) {
      this.props.onPlay();
    }
  };

  render() {
    if (this.state.showPlayer) {
      return (
        <YoutubePlayer
          allowFloating={this.props.allowFloating}
          hasAccess={this.props.hasAccess}
          url={this.props.url}
          placeholderImageUrl={this.props.placeholderImageUrl}
          podcastEpisode={this.props.podcastEpisode}
          autoPlay
          width={this.props.width}
          borderRadius={this.props.borderRadius}
        />
      );
    }

    return (
      <>
        <YoutubeThumbnail
          wrapperElement='button'
          onClick={this.handleThumbnailClick}
          title={this.props.title}
          borderRadius={this.props.borderRadius}
          thumbnailUrl={this.props.thumbnailUrl}
          thumbnailAlt={this.props.thumbnailAlt}
          durationSeconds={this.props.durationSeconds}
        />
        {
          isPresent(this.props.podcastEpisode) && this.props.hasAccess &&
            <PodcastButton
              hasAccess={this.props.hasAccess}
              width={AppUI.layout.isMobile ? '360px' : '540px'}
              margin='10px 0 0 0'
              height='50px'
              podcastEpisode={this.props.podcastEpisode}
            />
        }
      </>
    );
  }
}

export default YoutubeThumbnailPlayer;
